import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { isFuture } from 'date-fns'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type JobStore } from '@/features/jobs/JobStore'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { type BulkActionJobErrorDialogViewModel } from '@/features/jobs/list/components/BulkActionJobErrorDialog/BulkActionJobErrorDialogViewModel'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { pluralize } from '@/utils/pluralization-util'

/**
 * Confirms advancing the selected pending jobs and advances them.
 */
export class AdvanceJobsDialogViewModel {
  /**
   * View model for the underlying modal.
   */
  readonly modal = new ModalViewModel()

  @observable.ref private accessor _jobs: SearchableJob[] = []

  /**
   * Number of jobs that had not reached their advance date when the dialog
   * opened.
   */
  @observable accessor earlyAdvanceJobsCount = 0

  constructor(
    private readonly _jobStore: JobStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _getSelectedJobs: () => SearchableJob[],
    private readonly _onAdvanced: () => void,
    private readonly _errorDialogVm: BulkActionJobErrorDialogViewModel,
  ) {}

  /**
   * How many jobs are about to be advanced.
   */
  @computed
  get jobsCount() {
    return this._jobs.length
  }

  /**
   * Asks to confirm advancing the selected jobs.
   */
  @action.bound
  show() {
    this._jobs = this._getSelectedJobs()
    this.earlyAdvanceJobsCount = this._jobs.filter(
      (job) => job.dateAdvanceAllowed && isFuture(job.dateAdvanceAllowed),
    ).length
    this.modal.show()
  }

  /**
   * Advances the jobs.
   */
  advanceJobs = task.resolved(async () => {
    const jobs = this._jobs
    const alert = this._newAlert()
      .withState('pending')
      .saying(
        'Advancing jobs',
        `Advancing ${jobs.length} ${pluralize('job', jobs.length)}...`,
      )
      .show()
    const result = await this._jobStore
      .bulkAdvanceJobs(jobs.map((j) => j.id))
      .catch((err: unknown) => {
        alert.dismiss()
        return this._createAlertErrorHandler('Advancing the jobs failed')(err)
      })

    this.modal.close()

    const errors = [
      ...result.skippedIds.map((jobId) => ({
        jobId,
        error: 'Job is not pending',
      })),
      ...result.errors,
    ]
    if (errors.length > 0) {
      this._errorDialogVm.showErrors(errors, jobs)
    }

    this._onAdvanced()

    const advancedCount = result.advancedIds.length
    if (advancedCount === 0) {
      alert.dismiss()
      return
    }

    alert
      .withState('success')
      .saying(
        'Success!',
        `${advancedCount} ${pluralize('job', advancedCount)} ${pluralize('was', advancedCount, 'were')} advanced`,
      )
  })
}
